import { useFlagsmithLoading } from '@flagsmith/flagsmith/react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BASE_URL, MENU_URL } from '@/App';
import { EntryPromotionalContent } from '@/components/EntryPromotionalContent/EntryPromotionalContent';
import { LanguageSwitcher } from '@/components/LanguageSwitcher/LanguageSwitcher';
import { LinkToMenu } from '@/components/LinkToMenu/LinkToMenu';
import { useOutletKitchen } from '@/components/LinkToMenu/useOutletKitchen';
import Terms from '@/components/Terms/Terms';
import TrackedRedirectButton from '@/components/TrackedRedirectButton/TrackedRedirectButton';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useBrand } from '@/hooks/useBrand';
import { useBrowserLanguage } from '@/hooks/useBrowserLanguage';
import { useCustomerCheckIn } from '@/hooks/useCustomerCheckIn';
import { useCustomerMenuEnabled } from '@/hooks/useCustomerMenuEnabled';
import { useLanguage } from '@/hooks/useLanguage';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { sendBusinessEvent, setOutletContext } from '@/screens/Checkout/monitoring';
import { useBranding } from '@/utils/branding';
import { buildUrlWithParams } from '@/utils/queryParams';
import KitopiLogo from '../../assets/images/KitopiLogo.svg?react';
import Lines from '../../assets/images/Lines.svg';
import styles from './Home.module.scss';

const Home = () => {
  const { t } = useTranslation();
  const { data } = useBrand();
  const branding = useBranding();
  const { language } = useLanguage();
  const { queryParams } = useValidateQueryParams();
  const { tableNumber, counterNumber, outletId, brandId } = queryParams();
  const { mutate: checkIn } = useCustomerCheckIn();
  const { enabled: isCustomerMenuEnabled, isLoading: isLoadingIsCustomerMenuEnabled } =
    useCustomerMenuEnabled(brandId ?? undefined, outletId ?? undefined);
  const flagsmithLoadingState = useFlagsmithLoading();
  const { country } = useOutletKitchen(outletId);

  useBrowserLanguage(country);

  useEffect(() => {
    if (brandId && outletId && country) {
      setOutletContext({ brandId, outletId, country });
    }
  }, [brandId, outletId, country]);

  const screenViewedSentRef = useRef(false);
  useEffect(() => {
    if (!screenViewedSentRef.current && country) {
      sendBusinessEvent('kitopay.screen.viewed', {
        screen: 'entry',
        tableNumber,
        counterNumber,
      });
      screenViewedSentRef.current = true;
    }
  }, [country, tableNumber, counterNumber]);

  useEffect(() => {
    if (tableNumber) {
      checkIn();
    }
  }, [tableNumber, checkIn]);

  const payNowLink = useMemo(() => {
    return buildUrlWithParams('/entry', {
      tableNumber: tableNumber !== 'undefined' ? tableNumber : undefined,
      counterNumber: counterNumber !== 'undefined' ? counterNumber : undefined,
      outletId,
      brandId,
    });
  }, [tableNumber, counterNumber, outletId, brandId]);

  const menuLink = useMemo(() => {
    const hasOrderReference =
      (tableNumber && tableNumber !== 'undefined') ||
      (counterNumber && counterNumber !== 'undefined');

    return buildUrlWithParams(`/${language}${MENU_URL}`, {
      tableNumber: tableNumber !== 'undefined' ? tableNumber : undefined,
      counterNumber: counterNumber !== 'undefined' ? counterNumber : undefined,
      outletId,
      brandId,
      origin: hasOrderReference ? BASE_URL : undefined,
    });
  }, [tableNumber, counterNumber, outletId, brandId, language]);

  const buttonId = 'pay-now';
  const buttonName = t('menu.entry.payNow');

  const viewMenuButtonName = t('menu.entry.viewMenu');
  const viewMenuButtonId = 'view-menu-dine-in';

  const hasOrderIdentifier = Boolean(!!counterNumber || !!tableNumber);

  return (
    <div
      className={`${styles.entry}`}
      style={
        {
          '--entry-background-image': `url(${Lines})`,
        } as React.CSSProperties
      }
    >
      <div
        className={`${styles.backgroundLoader} ${!flagsmithLoadingState?.isLoading ? styles.hidden : ''}`}
      />
      <section className={styles.header}>
        <div className={`${styles.languageSwitcherContainer} ${styles.mobileOnly}`}>
          <LanguageSwitcher />
        </div>
        {branding.showKitopiLogo && (
          <h1>
            <KitopiLogo />
          </h1>
        )}
        <div className={styles.tableNumber}>
          {tableNumber ? (
            <span className={styles.tableNumberSpan}>
              {t('menu.brandPresentation.table')} {tableNumber?.replace('T', '')}
            </span>
          ) : (
            <span className={styles.tableNumberSpan}>{counterNumber}</span>
          )}
        </div>
      </section>
      <section className={styles.body}>
        {branding.showAcquisition ? (
          <EntryPromotionalContent />
        ) : (
          <div className={styles.brandLogo}>
            <img src={data?.logoUris['720_720']} alt={data?.name} />
          </div>
        )}
      </section>
      <section className={styles.footer}>
        {branding.showKitopiLogo && (
          <div className={styles.footerMenuLogo}>
            <img src={data?.logoUris['200_200']} alt={data?.name} />
          </div>
        )}
        {hasOrderIdentifier && (
          <TrackedRedirectButton
            buttonId={buttonId}
            buttonName={buttonName}
            buttonSource={BUTTON_SOURCES.WELCOME_SCREEN}
            className={styles.payNow}
            data-testid={buttonId}
            url={payNowLink}
            urlParams={{
              brandId: brandId || undefined,
              orderId: undefined,
              tableNumber,
              counterNumber,
            }}
          >
            {buttonName}
          </TrackedRedirectButton>
        )}
        {isCustomerMenuEnabled ? (
          <TrackedRedirectButton
            buttonId={viewMenuButtonId}
            buttonName={viewMenuButtonName}
            buttonSource={BUTTON_SOURCES.WELCOME_SCREEN}
            className={styles.white}
            data-testid={viewMenuButtonId}
            url={menuLink}
            urlParams={{
              brandId: brandId || undefined,
              orderId: undefined,
              tableNumber,
              counterNumber,
            }}
            isLoading={isLoadingIsCustomerMenuEnabled}
          />
        ) : (
          <LinkToMenu isLoading={isLoadingIsCustomerMenuEnabled} />
        )}

        <Terms />
      </section>
    </div>
  );
};

export default Home;
